import * as React from "react"
import { useEditor, EditorContent, useEditorState } from "@tiptap/react"
import type { JSONContent } from "@tiptap/react"
import {
  BoldIcon,
  ItalicIcon,
  LinkIcon,
  ListIcon,
  ListOrderedIcon,
} from "lucide-react"

import { FormFieldShell, type FormFieldProps } from "@/components/forms/FormField"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Toggle } from "@/components/ui/toggle"
import { richTextExtensions } from "@/utils/richTextExtensions"
import { cn } from "cn"

const EMPTY_DOC: JSONContent = {
  type: "doc",
  content: [{ type: "paragraph" }],
}

export type FormRichTextProps = FormFieldProps & {
  value?: Record<string, unknown> | JSONContent | null
  onChange?: (value: Record<string, unknown>) => void
  disabled?: boolean
  id?: string
  inputClassName?: string
  "aria-invalid"?: boolean | "true" | "false"
}

/**
 * Labeled TipTap editor (bold, italic, lists, link) with description / error
 * associations. Value is TipTap JSON — the same shape stored on pitch notes.
 *
 * Drive with `value` / `onChange` (react-hook-form `Controller` or local state).
 */
export function FormRichText({
  label,
  required,
  description,
  error,
  fieldClassName,
  inputClassName,
  id,
  disabled,
  value,
  onChange,
  "aria-invalid": ariaInvalid,
}: FormRichTextProps) {
  const generatedId = React.useId()
  const editorId = id ?? generatedId
  const invalid =
    Boolean(error) ||
    (ariaInvalid != null && ariaInvalid !== false && ariaInvalid !== "false")
  const descriptionId = description ? `${editorId}-description` : undefined
  const errorId = error ? `${editorId}-error` : undefined
  const ariaDescribedBy =
    [descriptionId, errorId].filter(Boolean).join(" ") || undefined

  const editor = useEditor({
    immediatelyRender: false,
    extensions: richTextExtensions(),
    content: asEditorDoc(value),
    editable: !disabled,
    editorProps: {
      attributes: {
        id: editorId,
        ...(ariaDescribedBy ? { "aria-describedby": ariaDescribedBy } : {}),
        ...(invalid ? { "aria-invalid": "true" } : {}),
      },
    },
    onUpdate: ({ editor: next }) => {
      onChange?.(next.getJSON() as Record<string, unknown>)
    },
  })

  React.useEffect(() => {
    if (!editor) return
    editor.setEditable(!disabled)
  }, [disabled, editor])

  React.useEffect(() => {
    if (!editor) return
    const next = asEditorDoc(value)
    if (JSON.stringify(editor.getJSON()) === JSON.stringify(next)) return
    editor.commands.setContent(next, { emitUpdate: false })
  }, [editor, value])

  return (
    <FormFieldShell
      label={label}
      required={required}
      description={description}
      error={error}
      fieldClassName={fieldClassName}
      htmlFor={editorId}
      invalid={invalid}
      disabled={disabled}
    >
      {() => (
        <div
          className={cn(
            "min-w-0 w-full rounded-md border border-input bg-transparent shadow-xs transition-[color,box-shadow] outline-none focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/30",
            invalid &&
              "border-destructive ring-3 ring-destructive/20 dark:border-destructive/50 dark:ring-destructive/40",
            disabled && "pointer-events-none cursor-not-allowed opacity-50",
            inputClassName
          )}
          data-disabled={disabled || undefined}
          data-invalid={invalid || undefined}
          onKeyDown={(event) => {
            // TipTap already applied bold. Stop the bubble so a page-level
            // Ctrl/Cmd+B listener (SidebarProvider) does not also toggle.
            if (
              (event.metaKey || event.ctrlKey) &&
              event.key.toLowerCase() === "b"
            ) {
              event.stopPropagation()
            }
          }}
        >
          {editor ? (
            <RichTextToolbar editor={editor} disabled={disabled} />
          ) : null}
          {editor ? (
            <EditorContent
              editor={editor}
              className={cn(
                "min-w-0 px-2.5 py-2 text-base md:text-sm",
                "[&_.tiptap]:min-h-16 [&_.tiptap]:min-w-0 [&_.tiptap]:outline-none",
                "[&_.ProseMirror]:min-h-16 [&_.ProseMirror]:min-w-0 [&_.ProseMirror]:break-words [&_.ProseMirror]:outline-none",
                "[&_.tiptap_ul]:list-disc [&_.tiptap_ol]:list-decimal [&_.tiptap_ul]:pl-5 [&_.tiptap_ol]:pl-5",
                "[&_.tiptap_a]:text-primary [&_.tiptap_a]:underline"
              )}
            />
          ) : (
            <div className="min-h-16 px-2.5 py-2" />
          )}
        </div>
      )}
    </FormFieldShell>
  )
}

function asEditorDoc(value?: Record<string, unknown> | JSONContent | null) {
  if (!value || typeof value !== "object") return EMPTY_DOC
  return value as JSONContent
}

function LinkToolbarButton({
  editor,
  disabled,
  isActive,
}: {
  editor: NonNullable<ReturnType<typeof useEditor>>
  disabled?: boolean
  isActive: boolean
}) {
  const [open, setOpen] = React.useState(false)
  const [href, setHref] = React.useState("")

  const applyLink = () => {
    const trimmed = href.trim()
    if (!trimmed) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run()
      setOpen(false)
      return
    }

    const next =
      /^https?:\/\//i.test(trimmed) || trimmed.startsWith("mailto:")
        ? trimmed
        : `https://${trimmed}`
    editor.chain().focus().extendMarkRange("link").setLink({ href: next }).run()
    setOpen(false)
  }

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen)
        if (nextOpen) {
          setHref(String(editor.getAttributes("link").href ?? ""))
        }
      }}
    >
      <PopoverTrigger
        disabled={disabled}
        render={
          <Toggle
            size="sm"
            pressed={isActive}
            aria-label="Link"
            disabled={disabled}
          />
        }
      >
        <LinkIcon />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72">
        <div className="space-y-3">
          <Input
            type="url"
            placeholder="https://"
            value={href}
            onChange={(event) => setHref(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== "Enter" || event.nativeEvent.isComposing) return
              event.preventDefault()
              applyLink()
            }}
            aria-label="Link URL"
          />
          <Button type="button" className="w-full" onClick={applyLink}>
            Apply
          </Button>
          {isActive ? (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => {
                editor.chain().focus().extendMarkRange("link").unsetLink().run()
                setHref("")
                setOpen(false)
              }}
            >
              Remove
            </Button>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  )
}

function RichTextToolbar({
  editor,
  disabled,
}: {
  editor: NonNullable<ReturnType<typeof useEditor>>
  disabled?: boolean
}) {
  const marks = useEditorState({
    editor,
    selector: ({ editor: current }) => ({
      bold: current.isActive("bold"),
      italic: current.isActive("italic"),
      bulletList: current.isActive("bulletList"),
      orderedList: current.isActive("orderedList"),
      link: current.isActive("link"),
    }),
  })

  // Row of formatting toggles — horizontal toolbar, not stacked fields.
  return (
    <div className="flex flex-wrap border-b border-input p-1">
      <Toggle
        size="sm"
        pressed={marks.bold}
        disabled={disabled}
        aria-label="Bold"
        onPressedChange={() => editor.chain().focus().toggleBold().run()}
      >
        <BoldIcon />
      </Toggle>
      <Toggle
        size="sm"
        pressed={marks.italic}
        disabled={disabled}
        aria-label="Italic"
        onPressedChange={() => editor.chain().focus().toggleItalic().run()}
      >
        <ItalicIcon />
      </Toggle>
      <Toggle
        size="sm"
        pressed={marks.bulletList}
        disabled={disabled}
        aria-label="Bulleted list"
        onPressedChange={() => editor.chain().focus().toggleBulletList().run()}
      >
        <ListIcon />
      </Toggle>
      <Toggle
        size="sm"
        pressed={marks.orderedList}
        disabled={disabled}
        aria-label="Numbered list"
        onPressedChange={() =>
          editor.chain().focus().toggleOrderedList().run()
        }
      >
        <ListOrderedIcon />
      </Toggle>
      <LinkToolbarButton
        editor={editor}
        disabled={disabled}
        isActive={marks.link}
      />
    </div>
  )
}
